import { useQueryClient } from '@tanstack/react-query';
import {
  savePayoutsToStorage,
  savePayoutsToLocalStorage,
  saveSettingsToStorage
} from '../storage';
import { payoutApiService } from '@/services/payoutApiService';
import { getAuthState } from '@/contexts/AuthContext';
import type { Payout } from '../../types/payout';
import type { GlobalSettings } from '../../types/settings';


export function usePayoutMutations() {
  const queryClient = useQueryClient();

  const invalidatePayouts = () => {
    queryClient.invalidateQueries({ queryKey: ['payouts'] });
  };

  const invalidateImportBatches = () => {
    queryClient.invalidateQueries({ queryKey: ['importBatches'] });
  };

  // Full replace (e.g. after data management import)
  const savePayouts = async (payouts: Payout[]) => {
    await savePayoutsToStorage(payouts);
    invalidatePayouts();
  };

  // Add a single payout
  const addPayout = async (payout: Payout) => {
    const { isAuthenticated } = getAuthState();
    if (isAuthenticated) {
      await payoutApiService.create(payout);
    } else {
      // Optimistic local update
      const current = await import('../storage').then((m) => m.loadPayoutsFromLocalStorage());
      await savePayoutsToLocalStorage([payout, ...current]);
    }
    invalidatePayouts();
  };

  // Update a single payout
  const updatePayout = async (payout: Payout) => {
    const { isAuthenticated } = getAuthState();
    if (isAuthenticated) {
      await payoutApiService.update(payout);
    } else {
      const current = await import('../storage').then((m) => m.loadPayoutsFromLocalStorage());
      const updated = current.map((p) => (p.id === payout.id ? payout : p));
      await savePayoutsToLocalStorage(updated);
    }
    invalidatePayouts();
  };

  // Delete a single payout
  const deletePayout = async (id: string) => {
    const { isAuthenticated } = getAuthState();
    if (isAuthenticated) {
      await payoutApiService.delete(id);
    } else {
      const current = await import('../storage').then((m) => m.loadPayoutsFromLocalStorage());
      await savePayoutsToLocalStorage(current.filter((p) => p.id !== id));
    }
    invalidatePayouts();
  };

  // Additive bulk import — single API call, authenticated users only.
  // Passing a batchLabel tracks the import as a deletable batch (see ImportBatchesList).
  const bulkAddPayouts = async (payouts: Payout[], batchLabel?: string) => {
    const result = await payoutApiService.bulkImport(payouts, { batchLabel, batchSource: 'csv' });
    invalidatePayouts();
    if (batchLabel) invalidateImportBatches();
    return result;
  };

  const saveSettings = async (settings: GlobalSettings) => {
    await saveSettingsToStorage(settings);
  };

  return { savePayouts, addPayout, updatePayout, deletePayout, saveSettings, bulkAddPayouts };
}
